import React, { useState, useEffect, useRef, useMemo } from "react";
import { Spin, Empty, Card, DatePicker, Switch, Space, Typography, Tabs } from "antd";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import Chart from "chart.js/auto";
import {
  getProductVariationOrderPrediction,
  getDailyProductOrderPrediction,
} from "../../../core/api/mediaplan";

dayjs.extend(isSameOrBefore);

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { TabPane } = Tabs;

const DEFAULT_START = dayjs();
const DEFAULT_END = dayjs().add(100, "day");

// Component to render a single chart for a campaign-variation combination
const StockEvolutionChart = ({ campaignData, variationId, variationCode, dateRange, showNewCampaign }) => {
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState(null);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Extract campaign details for API call
  const campaign = useMemo(() => {
    if (!campaignData) return null;
    
    return {
      variation: variationId,
      mailQty: campaignData.mailQty,
      rateOfResponse: campaignData.rateOfResponse,
      mailDate: campaignData.mailDate
        ? typeof campaignData.mailDate === "string"
          ? campaignData.mailDate.split("T")[0]
          : dayjs(campaignData.mailDate).format("YYYY-MM-DD")
        : null,
      // Ensure itemEstimation has at least one item with qtyPerOrder for the variation
      itemEstimation: campaignData.itemEstimation && campaignData.itemEstimation.length > 0
        ? campaignData.itemEstimation
        : [{
            variation: variationId,
            qtyPerOrder: 1, // Default value if none provided
          }],
    };
  }, [campaignData, variationId]);

  // Fetch stock data for this specific campaign-variation combination
  useEffect(() => {
    if (!campaign || !variationId || !dateRange) return;
    
    // Check if all required campaign variables are set before making the API call
    const hasRequiredCampaignData = campaign && 
      campaign.mailQty && 
      campaign.rateOfResponse && 
      campaign.mailDate;
    
    if (!hasRequiredCampaignData) {
      return;
    }

    const controller = new AbortController();
    const fetchData = async () => {
      try {
        setLoading(true);
        const [start, end] = dateRange;

        const dailyRes = await getDailyProductOrderPrediction(
          [variationId],
          start,
          end,
          [campaign],
          { signal: controller.signal }
        );

        if (dailyRes.data && dailyRes.data.length > 0) {
          setStockData(dailyRes.data[0]);
        }
      } catch (err) {
        if (err.name !== "AbortError") console.error("Error fetching stock evolution", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [campaign, variationId, dateRange]);

  // Create or update chart when stock data changes
  useEffect(() => {
    if (!chartRef.current || !stockData || !stockData.stock_data) return;

    // Destroy the old chart (if any)
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const labels = stockData.stock_data.map((d) => dayjs(d.date).format("MMM DD"));
    const stockValues = stockData.stock_data.map((d) => d.value);

    const datasets = [
      {
        label: "Zero Line",
        data: labels.map(() => 0),
        borderColor: "rgba(0,0,0,0.5)",
        borderDash: [5, 5],
        pointRadius: 0,
        fill: false,
        yAxisID: "y",
      },
      {
        label: "Existing Campaigns Stock",
        data: stockValues,
        borderColor: "rgb(53,162,235)",
        backgroundColor: "rgba(53,162,235,0.5)",
        pointRadius: 2,
        pointHoverRadius: 2,
        fill: false,
        yAxisID: "y",
        segment: {
          borderColor: (ctx) => {
            const { p0, p1 } = ctx;
            return p0.parsed.y < 0 || p1.parsed.y < 0 ? "rgb(255,0,0)" : "rgb(53,162,235)";
          },
        },
        pointBackgroundColor: (ctx) => (ctx.parsed.y < 0 ? "rgb(255,0,0)" : "rgb(53,162,235)"),
        pointBorderColor: (ctx) => (ctx.parsed.y < 0 ? "rgb(255,0,0)" : "rgb(53,162,235)"),
      },
    ];

    // Optional new campaign line
    if (showNewCampaign && stockData.new_campaign_stock_data?.length) {
      const newCampaignValues = stockData.new_campaign_stock_data.map((d) => d.value);
      datasets.push({
        label: "With New Campaign Stock",
        data: newCampaignValues,
        borderColor: "rgb(255,99,132)",
        backgroundColor: "rgba(16, 7, 9, 0.5)",
        borderDash: [3, 3],
        pointRadius: 2,
        pointHoverRadius: 2,
        fill: false,
        yAxisID: "y",
        segment: {
          borderColor: (ctx) => {
            const { p0, p1 } = ctx;
            return p0.parsed.y < 0 || p1.parsed.y < 0 ? "rgb(220,20,60)" : "rgb(255,99,132)";
          },
        },
        pointBackgroundColor: (ctx) => (ctx.parsed.y < 0 ? "rgb(220,20,60)" : "rgb(255,99,132)"),
        pointBorderColor: (ctx) => (ctx.parsed.y < 0 ? "rgb(220,20,60)" : "rgb(255,99,132)"),
      });
    }

    const ctx = chartRef.current.getContext("2d");
    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        stacked: false,
        plugins: {
          datalabels: {
            display: false,
          },
            
          title: {
            display: true,
            text: `Stock Evolution - ${variationCode}`,
            font: { size: 14 }
          },
          legend: { position: "top" },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => {
                const label = tooltipItem.dataset.label ?? "";
                const value = tooltipItem.parsed.y;
                return `${label}: ${new Intl.NumberFormat().format(value)}`;
              },
            },
          },
        },
        scales: {
          y: {
            title: { display: true, text: "Stock Level" },
            ticks: {
              callback: (value) => new Intl.NumberFormat().format(value),
            },
          },
          x: {
            title: { display: true, text: "Date" },
          },
        },
      },
    });
  }, [chartRef, stockData, showNewCampaign, variationCode]);

  if (loading) {
    return (
      <div className="d-flex justify-center items-center h-full" style={{ minHeight: 300 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!stockData) {
    return <Empty description="No stock evolution data available" style={{ minHeight: 300 }} />;
  }

  return (
    <div style={{ width: "100%", height: 400 }} className="w-full">
      <canvas ref={chartRef} />
    </div>
  );
};

const ProductStockEvolution = ({ campaigns }) => {
  /* ─────────────────────────── STATE */
  const [dateRange, setDateRange] = useState([DEFAULT_START, DEFAULT_END]);
  const [showNewCampaign, setShowNewCampaign] = useState(true);
  const [activeTab, setActiveTab] = useState("0");
  /* ─────────────────────────── MEMO */
  // Group campaigns by variation for easier rendering
  const campaignsByVariation = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return {};
    
    const result = {};
    
    campaigns.forEach(campaign => {
      if (!campaign.variation) return;
      
      // Handle direct variation field
      if (!result[campaign.variation]) {
        result[campaign.variation] = [];
      }
      result[campaign.variation].push(campaign);
      
      // Also check itemEstimation for additional variations
      if (campaign.itemEstimation && campaign.itemEstimation.length > 0) {
        campaign.itemEstimation.forEach(item => {
          const itemVariation = item.variation || item.product_variation_id || item.product_variation;
          if (itemVariation && itemVariation !== campaign.variation) {
            if (!result[itemVariation]) {
              result[itemVariation] = [];
            }
            // Only add if not already present
            if (!result[itemVariation].some(c => c === campaign)) {
              result[itemVariation].push(campaign);
            }
          }
        });
      }
    });
    
    return result;
  }, [campaigns]);
  
  // Get all unique variation IDs
  const variationIds = useMemo(() => Object.keys(campaignsByVariation), [campaignsByVariation]);
  
  // Fetch variation details
  const [variationDetails, setVariationDetails] = useState({});
  
  useEffect(() => {
    if (variationIds.length === 0) return;
    
    const fetchVariationDetails = async () => {
      try {
        const response = await getProductVariationOrderPrediction(variationIds);
        const details = {};
        
        if (response.data && response.data.length > 0) {
          response.data.forEach(variation => {
            details[variation.variation_id] = {
              code: variation.variation_code,
              id: variation.variation_id
            };
          });
        }
        
        setVariationDetails(details);
      } catch (error) {
        console.error("Error fetching variation details:", error);
      }
    };
    
    fetchVariationDetails();
  }, [variationIds]);

  /* ─────────────────────────── RENDER */
  if (!campaigns?.length) return <Empty description="No campaigns available" />;
  if (variationIds.length === 0) return <Empty description="No product variations found in campaigns" />;

  return (
    <Card>
      <Space direction="vertical" size="large" className="w-full" style={{  width:"100%" }}>
        {/* Global Controls */}
        <Space wrap align="center">
          <Text strong>Select period:</Text>
          <RangePicker
            allowClear={false}
            value={dateRange}
            onChange={(range) => {
              if (!range) return;
              const [start, end] = range;
              setDateRange([start.startOf("day"), end.endOf("day")]);
            }}
          />
          
          <Space align="center">
            <Switch checked={showNewCampaign} onChange={setShowNewCampaign} />
            <Text>Include new campaign impact</Text>
          </Space>
        </Space>
        
        {/* Tabs for each variation */}
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {variationIds.map((variationId, index) => {
            const variation = variationDetails[variationId] || { code: `Variation ${variationId}`, id: variationId };
            const campaignsForVariation = campaignsByVariation[variationId] || [];
            
            return (
              <TabPane tab={variation.code} key={index.toString()}>
                {campaignsForVariation.length === 0 ? (
                  <Empty description="No campaigns for this variation" />
                ) : (
                  <Space direction="vertical" size="large" className="w-full"  style={{ width: "100%" }}>
                    {campaignsForVariation.map((campaign, campaignIndex) => (
                      <Card 
                        key={campaignIndex} 
                        title={`Campaign ${campaign.name || campaign.id || campaignIndex + 1}`}
                        size="small"
                        style={{ width: "100%" }}
                      >
                        <StockEvolutionChart
                          campaignData={campaign}
                          variationId={parseInt(variationId)}
                          variationCode={variation.code}
                          dateRange={dateRange}
                          showNewCampaign={showNewCampaign}
                        />
                      </Card>
                    ))}
                  </Space>
                )}
              </TabPane>
            );
          })}
        </Tabs>
      </Space>
    </Card>
  );
};

export default ProductStockEvolution;
